import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Search } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { CultureBadge } from './CultureBadge'
import { cn } from '@/lib/utils'
import { showErrorToast } from '@/lib/toast'

import { getCatalogOptions } from '@/lib/api/v2/catalog-translations'
import type { CatalogOptionSetDto } from '@/types/catalog-translations'

/**
 * Read-only viewer for the catalog option sets stored in DB. Each set is a
 * collapsible group exposing its items with values, label keys and resolved
 * labels per culture. Missing translations are highlighted in red.
 */
export function OptionLabelsTable() {
  const [sets, setSets] = useState<CatalogOptionSetDto[]>([])
  const [cultures, setCultures] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const payload = await getCatalogOptions()
        if (cancelled) return
        setSets(payload.optionSets)
        setCultures(payload.supportedCultures)
        // Auto-expand all on first load — there are usually only a handful of sets.
        const allExpanded: Record<string, boolean> = {}
        for (const s of payload.optionSets) allExpanded[s.code] = true
        setExpanded(allExpanded)
      } catch {
        if (!cancelled) showErrorToast('Erreur lors du chargement des option sets.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return sets
    return sets.filter(s =>
      s.code.toLowerCase().includes(q) ||
      s.labelKey.toLowerCase().includes(q) ||
      s.items.some(i => i.value.toLowerCase().includes(q))
    )
  }, [sets, search])

  function toggle(code: string) {
    setExpanded(prev => ({ ...prev, [code]: !prev[code] }))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Search className="size-4 text-muted-foreground" />
        <Input
          data-testid="catalog-options-search-input"
          type="search"
          placeholder="Rechercher un option set ou une valeur…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-sm h-8"
        />
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} option set{filtered.length > 1 ? 's' : ''}
          {filtered.length !== sets.length && ` (sur ${sets.length})`}
        </span>
      </div>

      {loading && (
        <div className="rounded-md border bg-card px-3 py-6 text-center text-sm text-muted-foreground">
          Chargement…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="rounded-md border bg-card px-3 py-8 text-center text-sm text-muted-foreground">
          Aucun option set.
        </div>
      )}

      {!loading && filtered.map(set => (
        <OptionSetCard
          key={set.code}
          set={set}
          cultures={cultures}
          expanded={!!expanded[set.code]}
          onToggle={() => toggle(set.code)}
        />
      ))}
    </div>
  )
}

interface OptionSetCardProps {
  set: CatalogOptionSetDto
  cultures: string[]
  expanded: boolean
  onToggle: () => void
}

function OptionSetCard({ set, cultures, expanded, onToggle }: OptionSetCardProps) {
  return (
    <div className="rounded-md border bg-card overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        data-testid={`catalog-options-toggle-${set.code}`}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/30 text-left"
      >
        {expanded ? <ChevronDown className="size-4 text-muted-foreground" /> : <ChevronRight className="size-4 text-muted-foreground" />}
        <span className="font-mono text-sm font-medium">{set.code}</span>
        <Badge variant="secondary" className="text-[11px]">
          {set.items.length} item{set.items.length > 1 ? 's' : ''}
        </Badge>
        {set.missingCultures.length > 0 && (
          <Badge variant="destructive" className="text-[10px] ml-auto">
            {set.missingCultures.length} traduction{set.missingCultures.length > 1 ? 's' : ''} manquante{set.missingCultures.length > 1 ? 's' : ''}
          </Badge>
        )}
      </button>

      {expanded && (
        <div className="border-t">
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr className="text-left">
                <th className="px-3 py-1.5 font-medium text-xs w-[16px]"></th>
                <th className="px-3 py-1.5 font-medium text-xs">Valeur</th>
                <th className="px-3 py-1.5 font-medium text-xs">Clé i18n</th>
                {cultures.map(c => (
                  <th key={c} className="px-3 py-1.5 font-medium text-xs">
                    <CultureBadge culture={c} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {set.items.map(item => (
                <tr key={item.value} data-testid={`catalog-options-item-${set.code}-${item.value}`}>
                  <td className="px-3 py-1.5">
                    {item.icon && <span aria-hidden>{item.icon}</span>}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[12px]">{item.value}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-muted-foreground">{item.labelKey}</td>
                  {cultures.map(c => {
                    const missing = item.missingCultures.includes(c)
                    return (
                      <td key={c} className="px-3 py-1.5">
                        <span className={cn(
                          'text-sm',
                          missing ? 'text-destructive italic' : ''
                        )}>
                          {missing ? '(non traduit)' : (item.labels[c] || '—')}
                        </span>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
